import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaInfoCircle,
  FaCheckCircle,
  FaCreditCard,
  FaMobileAlt,
  FaSpinner,
  FaExclamationTriangle,
  FaReceipt,
  FaRedo,
  FaShieldAlt,
  FaLock,
  FaHeart,
} from 'react-icons/fa';
import PaystackPop from '@paystack/inline-js';
import { donationService } from '../../services/donationService';
import type { InitiateDonationResponse } from '../../services/donationService';
import logoBlack from '../../assets/images/logo-black.png';

// Paystack public key (safe to expose in frontend — it is NOT a secret)
const PAYSTACK_PUBLIC_KEY = import.meta.env.VITE_PAYSTACK_PUBLIC_KEY as string;

// ---------------------------------------------------------------------------
// Types & constants
// ---------------------------------------------------------------------------

type Currency      = 'KES' | 'USD';
type PaymentMethod = 'mpesa' | 'card';
type ModalState    =
  | 'idle' | 'initiating' | 'awaiting_card'
  | 'awaiting_mpesa' | 'verifying' | 'completed' | 'failed';

const MPESA_TIMEOUT_SECONDS = 120;   // 2 minutes — matches M-Pesa STK push window
const POLL_INTERVAL_MS      = 5000;  // poll every 5 s to avoid hammering Paystack verify API

const PRESET_AMOUNTS: Record<Currency, number[]> = {
  KES: [500, 1000, 2500, 5000],
  USD: [5, 10, 25, 50],
};

const CURRENCY_META: Record<Currency, { label: string; symbol: string; flag: string; min: number }> = {
  KES: { label: 'Kenyan Shilling', symbol: 'KSh', flag: '🇰🇪', min: 10 },
  USD: { label: 'US Dollar',       symbol: '$',   flag: '🇺🇸', min: 1  },
};

interface DonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCause?: { title: string; description: string; icon?: React.ReactNode };
}

const initialForm = { donorName: '', donorEmail: '', donorPhone: '', amount: '' };

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export const DonationModal: React.FC<DonationModalProps> = ({ isOpen, onClose, selectedCause }) => {
  const [form, setForm]                   = useState(initialForm);
  const [mpesaPhone, setMpesaPhone]       = useState('');
  const [currency, setCurrency]           = useState<Currency>('KES');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('mpesa');
  const [modalState, setModalState]       = useState<ModalState>('idle');
  const [errorMessage, setErrorMessage]   = useState<string | null>(null);
  const [receiptNumber, setReceiptNumber] = useState<string | null>(null);
  const [countdown, setCountdown]         = useState(MPESA_TIMEOUT_SECONDS);

  const apiRefRef            = useRef<string | null>(null);
  const pollIntervalRef      = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const meta = CURRENCY_META[currency];

  // When currency changes to USD → force card; back to KES → default M-Pesa
  useEffect(() => {
    setPaymentMethod(currency === 'USD' ? 'card' : 'mpesa');
    setForm((prev) => ({ ...prev, amount: '' }));
  }, [currency]);

  // When donorPhone changes, sync mpesaPhone if it hasn't been manually edited
  useEffect(() => {
    setMpesaPhone(form.donorPhone);
  }, [form.donorPhone]);

  // -------------------------------------------------------------------------
  const clearTimers = useCallback(() => {
    if (pollIntervalRef.current)      clearInterval(pollIntervalRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    pollIntervalRef.current = countdownIntervalRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen) clearTimers();
    return clearTimers;
  }, [isOpen, clearTimers]);

  // -------------------------------------------------------------------------
  const startPolling = useCallback((apiRef: string) => {
    apiRefRef.current = apiRef;
    pollIntervalRef.current = setInterval(async () => {
      try {
        const result = await donationService.getDonationStatus(apiRef);
        if (result.status === 'completed') {
          clearTimers(); setReceiptNumber(result.receipt_number ?? null); setModalState('completed');
        } else if (result.status === 'failed') {
          clearTimers(); setErrorMessage(result.failed_reason || 'Payment failed. Please try again.'); setModalState('failed');
        }
      } catch { /* keep polling */ }
    }, POLL_INTERVAL_MS);
  }, [clearTimers]);

  const startCountdown = useCallback((apiRef: string) => {
    setCountdown(MPESA_TIMEOUT_SECONDS);
    countdownIntervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownIntervalRef.current!);
          countdownIntervalRef.current = null;
          donationService.getDonationStatus(apiRef)
            .then((r) => {
              clearTimers();
              if (r.status === 'completed') { setReceiptNumber(r.receipt_number ?? null); setModalState('completed'); }
              else { setErrorMessage('M-Pesa payment timed out. Please try again.'); setModalState('failed'); }
            })
            .catch(() => { clearTimers(); setErrorMessage('M-Pesa payment timed out. Please try again.'); setModalState('failed'); });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearTimers]);

  // -------------------------------------------------------------------------
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!form.donorName || !form.donorEmail || !form.donorPhone || !form.amount) {
      setErrorMessage('Please fill in all required fields.'); return;
    }
    if (paymentMethod === 'mpesa' && !mpesaPhone) {
      setErrorMessage('Please enter your M-Pesa phone number.'); return;
    }
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount < meta.min) {
      setErrorMessage(`Minimum donation is ${meta.symbol}${meta.min}.`); return;
    }
    if (!selectedCause) {
      setErrorMessage('No cause selected.'); return;
    }

    // -----------------------------------------------------------------
    // CARD / APPLE PAY — use checkout() which auto-detects Apple Pay on
    // iOS / Safari and shows a pre-checkout modal with the Apple Pay
    // button first. On other browsers it loads the standard Paystack
    // checkout. Called directly inside the submit handler (user gesture)
    // so popup blockers are not triggered.
    // -----------------------------------------------------------------
    if (paymentMethod === 'card') {
      if (!PAYSTACK_PUBLIC_KEY) {
        setErrorMessage('Payment configuration missing. Please contact support.');
        setModalState('failed');
        return;
      }
      const amountKobo = Math.round(parseFloat(form.amount) * 100);
      setModalState('awaiting_card');
      const popup = new PaystackPop();
      await popup.checkout({
        key:      PAYSTACK_PUBLIC_KEY,
        email:    form.donorEmail,
        amount:   amountKobo,
        currency,
        channels: ['card', 'apple_pay'],
        metadata: {
          custom_fields: [
            { display_name: 'Donor Name', variable_name: 'donor_name', value: form.donorName },
            { display_name: 'Cause',      variable_name: 'cause',      value: selectedCause.title },
          ],
        },
        onSuccess: async (transaction: { reference: string }) => {
          setModalState('verifying');
          try {
            const result = await donationService.confirmCardDonation({
              donorName:          form.donorName,
              donorEmail:         form.donorEmail,
              donorPhone:         form.donorPhone,
              cause:              selectedCause.title,
              amount:             form.amount,
              currency,
              paystackReference:  transaction.reference,
            });
            setReceiptNumber(result.receipt_number ?? null);
            setModalState('completed');
          } catch (err: unknown) {
            // Payment WAS taken — do not say "try again", give support reference
            setErrorMessage(
              (err instanceof Error ? err.message : null)
              || `Your payment was received but we could not save the record.
                  Please contact us with reference: ${transaction.reference}`
            );
            setModalState('failed');
          }
        },
        onCancel: () => {
          // User closed the popup — go back to idle so they can retry
          setModalState('idle');
          setErrorMessage(null);
        },
        onError: (error: { message: string }) => {
          setErrorMessage(error.message || 'Card payment failed. Please try again.');
          setModalState('failed');
        },
      });
      return;
    }

    // -----------------------------------------------------------------
    // M-PESA payment — backend initiates STK push, then we poll status
    // -----------------------------------------------------------------
    setModalState('initiating');
    try {
      const result: InitiateDonationResponse = await donationService.initiateDonation({
        donorName: form.donorName, donorEmail: form.donorEmail,
        donorPhone: form.donorPhone, cause: selectedCause.title,
        amount: form.amount, currency, paymentMethod,
        mpesaPhone: mpesaPhone,
      });

      setModalState('awaiting_mpesa');
      // Small delay before first poll so the STK push has time to reach Paystack
      setTimeout(() => startPolling(result.api_ref), 5000);
      startCountdown(result.api_ref);
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Payment initiation failed. Please try again.');
      setModalState('failed');
    }
  };

  const handleClose = () => {
    clearTimers();
    setForm(initialForm); setMpesaPhone(''); setCurrency('KES');
    setPaymentMethod('mpesa'); setModalState('idle');
    setErrorMessage(null); setReceiptNumber(null);
    setCountdown(MPESA_TIMEOUT_SECONDS); apiRefRef.current = null;
    onClose();
  };

  const handleRetry = () => {
    clearTimers(); setModalState('idle'); setErrorMessage(null);
    setCountdown(MPESA_TIMEOUT_SECONDS); apiRefRef.current = null;
  };

  if (!isOpen) return null;

  const countdownMins = String(Math.floor(countdown / 60)).padStart(2, '0');
  const countdownSecs = String(countdown % 60).padStart(2, '0');
  const amountDisplay = form.amount ? `${meta.symbol}${parseFloat(form.amount).toLocaleString()}` : '...';

  // =========================================================================
  return (
    <div
      className="kf-modal__overlay"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
      role="dialog" aria-modal="true" aria-labelledby="donation-modal-title"
    >
      <div className="kf-dm" onClick={(e) => e.stopPropagation()}>

        {/* ── Gradient header ────────────────────────────────────────── */}
        <div className="kf-dm__header">
          <div className="kf-dm__header-inner">
            <div className="kf-dm__header-icon"><FaHeart /></div>
            <div>
              <h2 id="donation-modal-title" className="kf-dm__header-title">
                {modalState === 'completed' ? 'Donation Received!' : 'Make a Donation'}
              </h2>
              {selectedCause && modalState === 'idle' && (
                <p className="kf-dm__header-cause">{selectedCause.title}</p>
              )}
            </div>
          </div>
          <button type="button" className="kf-dm__close" onClick={handleClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* ── Body ───────────────────────────────────────────────────── */}
        <div className="kf-dm__body">

          {/* ============================================================
              IDLE — full form
              ============================================================ */}
          {modalState === 'idle' && (
            <form onSubmit={handleSubmit} className="kf-dm__form" noValidate>

              {errorMessage && (
                <div className="kf-dm__alert kf-dm__alert--error" role="alert">
                  <FaExclamationTriangle /><span>{errorMessage}</span>
                </div>
              )}

              {/* ── Step 1: Amount ──────────────────────────────────── */}
              <div className="kf-dm__step">
                <div className="kf-dm__step-header">
                  <span className="kf-dm__step-num">1</span>
                  <span className="kf-dm__step-label">Donation Amount</span>
                </div>

                {/* Currency toggle */}
                <div className="kf-dm__currency-row" role="group" aria-label="Select currency">
                  {(['KES', 'USD'] as Currency[]).map((cur) => {
                    const m = CURRENCY_META[cur];
                    return (
                      <button
                        key={cur} type="button"
                        className={`kf-dm__currency-btn${currency === cur ? ' kf-dm__currency-btn--active' : ''}`}
                        onClick={() => setCurrency(cur)} aria-pressed={currency === cur}
                      >
                        <span className="kf-dm__currency-flag">{m.flag}</span>
                        <span className="kf-dm__currency-code">{cur}</span>
                        <span className="kf-dm__currency-name">{m.label}</span>
                        {currency === cur && <FaCheckCircle className="kf-dm__currency-check" />}
                      </button>
                    );
                  })}
                </div>

                {/* Preset amounts */}
                <div className="kf-dm__presets">
                  {PRESET_AMOUNTS[currency].map((amt) => (
                    <button
                      key={amt} type="button"
                      className={`kf-dm__preset-btn${form.amount === String(amt) ? ' kf-dm__preset-btn--active' : ''}`}
                      onClick={() => setForm((p) => ({ ...p, amount: String(amt) }))}
                    >
                      {meta.symbol}{amt.toLocaleString()}
                    </button>
                  ))}
                </div>

                {/* Custom amount */}
                <div className="kf-dm__amount-wrapper">
                  <span className="kf-dm__amount-prefix">{meta.symbol}</span>
                  <input
                    id="donation-amount" type="number" name="amount"
                    value={form.amount}
                    onChange={handleChange}
                    placeholder={currency === 'KES' ? 'Enter amount…' : 'Enter amount…'}
                    min={meta.min} step="any" required
                    className="kf-dm__amount-input"
                    aria-label={`Donation amount in ${currency}`}
                  />
                  <span className="kf-dm__amount-suffix">{currency}</span>
                </div>
                <p className="kf-dm__hint"><FaInfoCircle /> Minimum {meta.symbol}{meta.min}</p>
              </div>

              {/* ── Step 2: Donor details ────────────────────────────── */}
              <div className="kf-dm__step">
                <div className="kf-dm__step-header">
                  <span className="kf-dm__step-num">2</span>
                  <span className="kf-dm__step-label">Your Details</span>
                </div>

                <div className="kf-dm__field-row">
                  <div className="kf-dm__field">
                    <label htmlFor="donor-name" className="kf-dm__label">
                      <FaUser className="kf-dm__label-icon" /> Full Name <span>*</span>
                    </label>
                    <input id="donor-name" type="text" name="donorName"
                      value={form.donorName} onChange={handleChange}
                      placeholder="e.g. Jane Mwangi" required className="kf-dm__input" />
                  </div>
                  <div className="kf-dm__field">
                    <label htmlFor="donor-email" className="kf-dm__label">
                      <FaEnvelope className="kf-dm__label-icon" /> Email <span>*</span>
                    </label>
                    <input id="donor-email" type="email" name="donorEmail"
                      value={form.donorEmail} onChange={handleChange}
                      placeholder="e.g. jane@example.com" required className="kf-dm__input" />
                  </div>
                </div>

                <div className="kf-dm__field">
                  <label htmlFor="donor-phone" className="kf-dm__label">
                    <FaPhone className="kf-dm__label-icon" /> Contact Phone <span>*</span>
                  </label>
                  <input id="donor-phone" type="tel" name="donorPhone"
                    value={form.donorPhone} onChange={handleChange}
                    placeholder="e.g. 0722 123 456" required className="kf-dm__input" />
                </div>
              </div>

              {/* ── Step 3: Payment method ───────────────────────────── */}
              <div className="kf-dm__step">
                <div className="kf-dm__step-header">
                  <span className="kf-dm__step-num">3</span>
                  <span className="kf-dm__step-label">Payment Method</span>
                </div>

                <div className="kf-dm__payment-options">

                  {/* M-Pesa card — KES only */}
                  {currency === 'KES' && (
                    <div
                      className={`kf-dm__pay-card kf-dm__pay-card--mpesa${paymentMethod === 'mpesa' ? ' kf-dm__pay-card--selected' : ''}`}
                      onClick={() => setPaymentMethod('mpesa')}
                      role="button" tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && setPaymentMethod('mpesa')}
                    >
                      <div className="kf-dm__pay-card-top">
                        <div className="kf-dm__pay-card-info">
                          <div className="kf-dm__pay-icon kf-dm__pay-icon--mpesa">
                            <FaMobileAlt />
                          </div>
                          <div>
                            <div className="kf-dm__pay-name">M-Pesa</div>
                            <div className="kf-dm__pay-desc">STK push to your phone</div>
                          </div>
                        </div>
                        <div className={`kf-dm__pay-radio${paymentMethod === 'mpesa' ? ' kf-dm__pay-radio--checked' : ''}`}>
                          {paymentMethod === 'mpesa' && <span className="kf-dm__pay-radio-dot" />}
                        </div>
                      </div>

                      {/* Expanded: M-Pesa phone input */}
                      {paymentMethod === 'mpesa' && (
                        <div className="kf-dm__pay-card-body" onClick={(e) => e.stopPropagation()}>
                          <label htmlFor="mpesa-phone" className="kf-dm__label" style={{ marginTop: 0 }}>
                            <FaPhone className="kf-dm__label-icon" /> M-Pesa Number <span>*</span>
                          </label>
                          <div className="kf-dm__mpesa-input-row">
                            <span className="kf-dm__mpesa-prefix">
                              <span className="kf-dm__mpesa-flag">🇰🇪</span>
                              <span className="kf-dm__mpesa-code">+254</span>
                            </span>
                            <input
                              id="mpesa-phone" type="tel"
                              value={mpesaPhone} onChange={(e) => setMpesaPhone(e.target.value)}
                              placeholder="0722 123 456" required={paymentMethod === 'mpesa'}
                              className="kf-dm__mpesa-input"
                            />
                          </div>
                          <p className="kf-dm__hint" style={{ marginTop: 6 }}>
                            <FaInfoCircle /> You will receive an M-Pesa PIN prompt on this number.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Card / Apple Pay */}
                  <div
                    className={`kf-dm__pay-card kf-dm__pay-card--card${paymentMethod === 'card' ? ' kf-dm__pay-card--selected' : ''}`}
                    onClick={() => setPaymentMethod('card')}
                    role="button" tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && setPaymentMethod('card')}
                  >
                    <div className="kf-dm__pay-card-top">
                      <div className="kf-dm__pay-card-info">
                        <div className="kf-dm__pay-icon kf-dm__pay-icon--card">
                          <FaCreditCard />
                        </div>
                        <div>
                          <div className="kf-dm__pay-name">Card / Apple Pay</div>
                          <div className="kf-dm__pay-brands">
                            <span className="kf-dm__brand kf-dm__brand--visa">VISA</span>
                            <span className="kf-dm__brand kf-dm__brand--mc">MC</span>
                            <span className="kf-dm__brand kf-dm__brand--amex">AMEX</span>
                            <span className="kf-dm__brand kf-dm__brand--disc">DISC</span>
                            <span className="kf-dm__brand kf-dm__brand--applepay"> Pay</span>
                          </div>
                        </div>
                      </div>
                      <div className={`kf-dm__pay-radio${paymentMethod === 'card' ? ' kf-dm__pay-radio--checked' : ''}`}>
                        {paymentMethod === 'card' && <span className="kf-dm__pay-radio-dot" />}
                      </div>
                    </div>
                    {paymentMethod === 'card' && (
                      <div className="kf-dm__pay-card-body">
                        <p className="kf-dm__hint" style={{ marginTop: 0 }}>
                          <FaLock /> Secure Paystack checkout — Apple Pay available on supported Apple devices.
                          {currency === 'USD' && <> You will be charged in <strong>USD</strong>.</>}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Submit + security ───────────────────────────────── */}
              <div className="kf-dm__footer">
                <button type="button" className="kf-dm__btn-secondary" onClick={handleClose}>
                  Cancel
                </button>
                <button type="submit" className="kf-dm__btn-primary">
                  <FaLock />
                  {paymentMethod === 'mpesa'
                    ? `Pay ${amountDisplay} via M-Pesa`
                    : `Pay ${amountDisplay} by Card / Apple Pay`}
                </button>
              </div>

              <div className="kf-dm__trust">
                <span className="kf-dm__trust-item"><FaShieldAlt /> Secured by Paystack</span>
                <span className="kf-dm__trust-divider" />
                <span className="kf-dm__trust-item"><FaLock /> 256-bit SSL</span>
                <span className="kf-dm__trust-divider" />
                <span className="kf-dm__trust-item"><FaCheckCircle /> Encrypted payments</span>
              </div>
            </form>
          )}

          {/* ============================================================
              INITIATING
              ============================================================ */}
          {modalState === 'initiating' && (
            <div className="kf-dm__status">
              <div className="kf-dm__status-icon kf-dm__status-icon--spin"><FaSpinner /></div>
              <h3>Preparing your payment…</h3>
              <p>Setting up your secure payment. Please wait.</p>
            </div>
          )}

          {/* ============================================================
              AWAITING CARD
              ============================================================ */}
          {modalState === 'awaiting_card' && (
            <div className="kf-dm__status">
              <div className="kf-dm__status-icon kf-dm__status-icon--card"><FaCreditCard /></div>
              <h3>Checkout Open</h3>
              <p>Complete your payment in the Paystack window. Apple Pay is available on supported Apple devices. This page updates automatically once confirmed.</p>
              <div className="kf-dm__pulse-ring" />
              <div className="kf-dm__live-badge"><span className="kf-dm__live-dot" /> Waiting for payment…</div>
            </div>
          )}

          {/* ============================================================
              AWAITING M-PESA
              ============================================================ */}
          {modalState === 'awaiting_mpesa' && (
            <div className="kf-dm__status kf-dm__status--mpesa">
              <div className="kf-dm__status-icon kf-dm__status-icon--mpesa"><FaMobileAlt /></div>
              <h3>Check Your Phone</h3>
              <p>
                An M-Pesa prompt has been sent to <strong>{mpesaPhone || form.donorPhone}</strong>.<br />
                Enter your M-Pesa PIN to complete the donation.
              </p>

              <div className="kf-dm__countdown">
                <p className="kf-dm__countdown-label">Time remaining to complete payment</p>
                <div className={`kf-dm__countdown-time${countdown <= 30 ? ' kf-dm__countdown-time--urgent' : ''}`}>
                  {countdownMins}:{countdownSecs}
                </div>
                <div className="kf-dm__countdown-bar">
                  <div
                    className="kf-dm__countdown-fill"
                    style={{ width: `${(countdown / MPESA_TIMEOUT_SECONDS) * 100}%` }}
                  />
                </div>
              </div>

              <div className="kf-dm__live-badge"><span className="kf-dm__live-dot" /> Waiting for confirmation…</div>

              <div className="kf-dm__mpesa-steps">
                <div className="kf-dm__mpesa-step"><span className="kf-dm__mpesa-step-num">1</span> Open M-Pesa on your phone</div>
                <div className="kf-dm__mpesa-step"><span className="kf-dm__mpesa-step-num">2</span> Enter your M-Pesa PIN</div>
                <div className="kf-dm__mpesa-step"><span className="kf-dm__mpesa-step-num">3</span> Confirm the transaction</div>
              </div>
            </div>
          )}

          {/* ============================================================
              VERIFYING
              ============================================================ */}
          {modalState === 'verifying' && (
            <div className="kf-dm__status">
              <div className="kf-dm__status-icon kf-dm__status-icon--spin"><FaSpinner /></div>
              <h3>Verifying Payment…</h3>
              <p>Confirming with Paystack. This will only take a moment.</p>
            </div>
          )}

          {/* ============================================================
              COMPLETED
              ============================================================ */}
          {modalState === 'completed' && (
            <div className="kf-dm__status kf-dm__status--success">
              <div className="kf-dm__status-icon kf-dm__status-icon--success"><FaCheckCircle /></div>
              <h3>Thank You!</h3>
              <p>Your donation to <strong>{selectedCause?.title}</strong> has been confirmed.<br />May God bless your generosity.</p>

              {receiptNumber && (
                <div className="kf-dm__receipt">

                  {/* ── Letterhead ──────────────────────────────── */}
                  <div className="kf-dm__receipt-letterhead">
                    <img src={logoBlack} alt="Kenya Friars" className="kf-dm__receipt-logo" />
                    <div className="kf-dm__receipt-org">CONVENTUAL FRANCISCAN FRIARS — KENYA</div>
                    <div className="kf-dm__receipt-slogan">In the Footsteps of St. Francis of Assisi</div>
                  </div>

                  {/* ── Receipt title ───────────────────────────── */}
                  <div className="kf-dm__receipt-head">
                    <FaReceipt /> Donation Receipt
                  </div>

                  {/* ── Receipt body ────────────────────────────── */}
                  <div className="kf-dm__receipt-grid">
                    <div className="kf-dm__receipt-row">
                      <span>Receipt No.</span>
                      <strong>{receiptNumber}</strong>
                    </div>
                    <div className="kf-dm__receipt-row">
                      <span>Cause</span>
                      <strong>{selectedCause?.title}</strong>
                    </div>
                    <div className="kf-dm__receipt-row">
                      <span>Amount</span>
                      <strong>{meta.symbol}{parseFloat(form.amount).toLocaleString()} {currency}</strong>
                    </div>
                    <div className="kf-dm__receipt-row">
                      <span>Method</span>
                      <strong>{paymentMethod === 'mpesa' ? 'M-Pesa' : `Card / Apple Pay (${currency})`}</strong>
                    </div>
                  </div>

                  {/* ── Footer ──────────────────────────────────── */}
                  <div className="kf-dm__receipt-footer">
                    <p className="kf-dm__receipt-footer-note">
                      This receipt is system-generated and does not require a stamp or signature.
                    </p>
                    <div className="kf-dm__receipt-contact">
                      <span>P.O. Box 3663 – 00506, Nairobi, Kenya</span>
                      <span className="kf-dm__receipt-contact-sep">·</span>
                      <span>Tel: (+254) 724 16 44 99</span>
                      <span className="kf-dm__receipt-contact-sep">·</span>
                      <span>kenprovincial@gmail.com</span>
                      <span className="kf-dm__receipt-contact-sep">·</span>
                      <span>www.kenyafriars.org</span>
                    </div>
                  </div>

                </div>
              )}

              <div className="kf-dm__footer" style={{ marginTop: 24 }}>
                <button type="button" className="kf-dm__btn-primary" onClick={handleClose}>
                  <FaCheckCircle /> Done
                </button>
              </div>
            </div>
          )}

          {/* ============================================================
              FAILED
              ============================================================ */}
          {modalState === 'failed' && (
            <div className="kf-dm__status kf-dm__status--failed">
              <div className="kf-dm__status-icon kf-dm__status-icon--failed"><FaExclamationTriangle /></div>
              <h3>Payment Not Completed</h3>
              <p>{errorMessage || 'The payment was not completed. Please try again.'}</p>
              <div className="kf-dm__footer" style={{ marginTop: 24 }}>
                <button type="button" className="kf-dm__btn-secondary" onClick={handleClose}>Close</button>
                <button type="button" className="kf-dm__btn-primary" onClick={handleRetry}><FaRedo /> Try Again</button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};
